import { ReadWaiveAnswer } from '@/types'

// Pure decision helpers for the closing step's transcript-order behaviors.
// Extracted from DefaultClosingForm so the three regression-prone rules —
// form→state sync routing, order-confirmation invalidation, and the
// collect-before-confirm gate — can be unit tested without rendering.

export type ClosingSyncValues = {
  result?: string | null
  read_waive_answer?: ReadWaiveAnswer | null
  errata_recipient_attendee_id?: string | null
  order_transcript?: boolean | null
  attendees?: unknown
  spellings?: string | null
  timestamps?: string | null
}

export type ClosingSyncEffect =
  | { type: 'clearTranscriptOrders' }
  | { type: 'readWaiveAnswer'; value: ReadWaiveAnswer }
  | { type: 'errataRecipient'; value: string | null }
  | { type: 'orderTranscript'; value: boolean }
  | { type: 'attendees'; attendees: unknown }
  | { type: 'spellings'; value: string | null | undefined }
  | { type: 'timestamps'; value: string | null | undefined }
  | null

// Map a react-hook-form watch event (changed field `name` + current form
// `value`) to the script-state action that should run. Nested attendee edits
// (e.g. the confirm step's `attendees.0.email`) must route to the attendees
// sync just like a whole-array `attendees` change — otherwise those edits
// never reach script state.
export const resolveClosingSyncEffect = (
  name: string | undefined,
  value: ClosingSyncValues,
): ClosingSyncEffect => {
  if (name === 'result' && value.result !== 'canceled_cna') {
    return { type: 'clearTranscriptOrders' }
  }

  if (
    name === 'read_waive_answer' &&
    value.read_waive_answer !== undefined &&
    value.read_waive_answer !== null
  ) {
    return {
      type: 'readWaiveAnswer',
      value: value.read_waive_answer,
    }
  }

  if (name === 'errata_recipient_attendee_id') {
    return {
      type: 'errataRecipient',
      value: value.errata_recipient_attendee_id ?? null,
    }
  }

  if (name === 'order_transcript') {
    return { type: 'orderTranscript', value: Boolean(value.order_transcript) }
  }

  if (name === 'attendees' || name?.startsWith('attendees.')) {
    return { type: 'attendees', attendees: value.attendees }
  }

  if (name === 'spellings') {
    return { type: 'spellings', value: value.spellings }
  }

  if (name === 'timestamps') {
    return { type: 'timestamps', value: value.timestamps }
  }

  return null
}

// A confirmation reflects the orders as they stood when it was made, so any
// change to the collected orders must invalidate it and force the reporter to
// re-confirm the up-to-date orders with attendees.
export const undoOrderConfirmationIfCompleted = (
  isActionCompleted: (key: string) => boolean,
  undoAction: (key: string) => void,
): void => {
  if (isActionCompleted('confirm_orders')) {
    undoAction('confirm_orders')
  }
}

// The recipient is cleared with any answer other than Read, so a confirmation
// made before the witness changed their mind has to go with it. Otherwise a
// read -> waive -> read correction leaves the step ticked with nothing
// recorded, and the recipient itself is optional, so nothing stops the
// reporter continuing.
export const undoErrataRecipientIfCompleted = (
  isActionCompleted: (key: string) => boolean,
  undoAction: (key: string) => void,
): void => {
  if (isActionCompleted('select_errata_recipient')) {
    undoAction('select_errata_recipient')
  }
}

const TRANSCRIPT_ORDER_ACTIONS = [
  'collect_transcript_orders',
  'confirm_orders',
] as const

export const undoTranscriptOrderActions = (
  isActionCompleted: (key: string) => boolean,
  undoAction: (key: string) => void,
): void => {
  TRANSCRIPT_ORDER_ACTIONS.forEach(actionKey => {
    if (isActionCompleted(actionKey)) {
      undoAction(actionKey)
    }
  })
}

export const clearAttendeeTranscriptOrders = <
  T extends { transcript_order?: unknown },
>(
  attendees: T[],
): T[] =>
  attendees.map(attendee => ({
    ...attendee,
    transcript_order: null,
  }))

export type ConfirmOrdersGate =
  | { allowed: true }
  | {
      allowed: false
      notification: { color: string; title: string; message: string }
    }

// Orders can only be confirmed once they have been collected, so gate the
// confirm action on the collect action being complete.
export const gateConfirmOrders = (
  isCollectCompleted: boolean,
): ConfirmOrdersGate => {
  if (!isCollectCompleted) {
    return {
      allowed: false,
      notification: {
        color: 'red',
        title: 'Collect transcript orders first',
        message:
          'Complete “Collect transcript orders” before confirming orders with attendees.',
      },
    }
  }

  return { allowed: true }
}
